/**
 * units.js — single source of truth for unit display.
 *
 * Storage convention (v1.70+): every `_in` field on a project / equipment
 * / tray is stored in INCHES; every `_lbs` field in POUNDS. The project's
 * `unit_system` is purely a DISPLAY preference. These helpers render those
 * canonical numbers in the user's preferred unit with explicit suffixes —
 * never bare numbers.
 */

export const IN_PER_M = 39.3701;
export const LB_PER_KG = 2.20462;

/** True when the project (or freeform option object) wants metric display. */
export function isMetric(projectOrOpts) {
  return projectOrOpts?.unit_system === "metric";
}

/** Format a length value (stored inches) as "X in" or "X.XX m". */
export function fmtLen(inches, opts = {}) {
  const metric = isMetric(opts);
  const v = Number(inches) || 0;
  if (metric) return `${(v / IN_PER_M).toFixed(opts.precision ?? 2)} m`;
  return `${v.toFixed(opts.precision ?? 0)} in`;
}

/** Length but using the `"` glyph for inches and `m` for metric — shorter
 *  form for plan/SVG labels where every character matters. */
export function fmtLenShort(inches, opts = {}) {
  const metric = isMetric(opts);
  const v = Number(inches) || 0;
  if (metric) return `${(v / IN_PER_M).toFixed(opts.precision ?? 2)} m`;
  return `${v.toFixed(opts.precision ?? 0)}″`;
}

/** Format weight (stored lbs). */
export function fmtWeight(lbs, opts = {}) {
  const metric = isMetric(opts);
  const v = Number(lbs) || 0;
  if (metric) return `${(v / LB_PER_KG).toFixed(opts.precision ?? 1)} kg`;
  return `${v.toFixed(opts.precision ?? 0)} lb`;
}

/** Unit label only — for input field suffixes. */
export const unitLen = (opts) => (isMetric(opts) ? "m" : "in");
export const unitWeight = (opts) => (isMetric(opts) ? "kg" : "lb");
